import { FileText, Copy, Clock, Network } from 'lucide-react'
import { useState } from 'react'
import {
  usePlanWizardStore,
  PlanCreationMethod,
} from '@/stores/usePlanWizardStore'
import { SelectableCard } from '@/components/common/SelectableCard'
import { StepHeader } from './StepHeader'

interface MethodOption {
  value: PlanCreationMethod
  icon: React.ReactNode
  title: string
  description: string
}

const methods: MethodOption[] = [
  {
    value: 'blank',
    icon: <FileText className="h-6 w-6" />,
    title: 'Create from Blank',
    description:
      'Start a fresh plan with no pre-filled data. Best for entirely new assortments.',
  },
  {
    value: 'copy',
    icon: <Copy className="h-6 w-6" />,
    title: 'Create from Copy',
    description:
      'Duplicate an existing plan structure and items. Good for iterations.',
  },
  {
    value: 'historicalData',
    icon: <Clock className="h-6 w-6" />,
    title: 'Create from Historical Data',
    description:
      "Import sales and performance data from previous year's records.",
  },
  {
    value: 'planogram',
    icon: <Network className="h-6 w-6" />,
    title: 'Create from Planogram',
    description: 'Import SKUs within one or more planograms.',
  },
]

export function MethodSelectionStep() {
  const { selectedMethod, setMethod } = usePlanWizardStore()
  const [localSelection, setLocalSelection] =
    useState<PlanCreationMethod>(selectedMethod)

  const handleSelect = (method: PlanCreationMethod) => {
    setLocalSelection(method)
    setMethod(method)
  }

  return (
    <div className="flex flex-col gap-[16px] items-center w-full">
      {/* Header */}
      <StepHeader
        title="How would you like to start?"
        description="Select a creation method to get started"
        className="flex flex-col gap-[4px] items-center text-center"
      />

      {/* Method cards */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-[16px] w-full max-w-7xl">
        {methods.map(method => (
          <SelectableCard
            key={method.value}
            selected={localSelection === method.value}
            onSelect={() => handleSelect(method.value)}
            icon={method.icon}
            title={method.title}
            description={method.description}
            variant="vertical"
          />
        ))}
      </div>
    </div>
  )
}
